import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import {
  MdArrowBack, MdEdit, MdDelete, MdInfoOutline,
  MdAccessTime, MdBarChart, MdTimeline, MdCheckBox, MdReceipt, MdAdd,
  MdCheckCircle, MdAddCircle, MdAttachMoney, MdClearAll
} from "react-icons/md";
import { supabase } from "../../../../lib/supabaseClient";
import {
  fetchActivities, fetchInvoices, addInvoice, updateInvoice, deleteInvoice,
  logActivity, clearActivities
} from "../../../../hooks/useProjects";
import TimeSheetPanel from "./TimeSheetPanel";
import TaskBoard from "./TaskBoard";
import CreateProjectModal from "./CreateProjectModal";

// ── Constants ──────────────────────────────────────────────
const STATUS_CFG = {
  Ongoing: { bg: "#E8F5E9", text: "#2E7D32" },
  Delayed: { bg: "#FFF8E1", text: "#E65100" },
  Stopped: { bg: "#FFEBEE", text: "#B71C1C" },
};

const fmt     = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "SAR", maximumFractionDigits: 0 }).format(n || 0);
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—";
const relTime = (d) => {
  if (!d) return "";
  const diff = Date.now() - new Date(d).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
};
const initials = (name) => (name || "?").split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2);

const TABS = [
  { id: "overview",  label: "Overview",   icon: MdBarChart   },
  { id: "timesheet", label: "Time Sheet", icon: MdAccessTime },
  { id: "tasks",     label: "Tasks",      icon: MdCheckBox   },
  { id: "activity",  label: "Activity",   icon: MdTimeline   },
];

// ── Billing Section ────────────────────────────────────────
function BillingSection({ project, currentUser }) {
  const [invoices,  setInvoices]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [addingRow, setAddingRow] = useState(false);
  const [newInv,    setNewInv]    = useState({ invoice_number: "", issue_date: "", amount: "", notes: "" });
  const [saving,    setSaving]    = useState(false);

  const load = useCallback(async () => {
    try { setInvoices(await fetchInvoices(project.id)); }
    catch {} finally { setLoading(false); }
  }, [project.id]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    if (!newInv.invoice_number.trim() || !newInv.amount) return;
    setSaving(true);
    try {
      await addInvoice({ projectId: project.id, invoiceNumber: newInv.invoice_number, issueDate: newInv.issue_date || null, amount: newInv.amount, notes: newInv.notes });
      await logActivity({ projectId: project.id, employeeId: currentUser?.id, actionType: "create", description: `Added invoice ${newInv.invoice_number} for ${fmt(newInv.amount)}` });
      setNewInv({ invoice_number: "", issue_date: "", amount: "", notes: "" });
      setAddingRow(false);
      await load();
    } catch (err) { alert(err.message); }
    finally { setSaving(false); }
  };

  const toggle = async (inv, field) => {
    setInvoices(prev => prev.map(i => i.id === inv.id ? { ...i, [field]: !inv[field] } : i));
    try { 
      await updateInvoice(inv.id, { [field]: !inv[field] }); 
      await logActivity({ projectId: project.id, employeeId: currentUser?.id, actionType: "update", description: `Marked invoice ${inv.invoice_number} as ${field === "is_issued" ? "issued" : "collected"}` });
    }
    catch { await load(); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this invoice?")) return;
    const inv = invoices.find(i => i.id === id);
    setInvoices(prev => prev.filter(i => i.id !== id));
    try { 
      await deleteInvoice(id); 
      if (inv) await logActivity({ projectId: project.id, employeeId: currentUser?.id, actionType: "delete", description: `Deleted invoice ${inv.invoice_number}` });
    } catch { await load(); }
  };

  const totalAmount    = invoices.reduce((s, i) => s + parseFloat(i.amount || 0), 0);
  const totalCollected = invoices.filter(i => i.is_collected).reduce((s, i) => s + parseFloat(i.amount || 0), 0);
  const remaining      = totalAmount - totalCollected;
  const unInvoiced     = (project.totalContractValue || 0) - totalAmount;
  const S = { padding: "5px 8px", border: "1px solid #e0e0ee", borderRadius: "6px", fontSize: "12px", outline: "none", fontFamily: "inherit", background: "#fafafa" };

  return (
    <div style={{ background: "#fff", borderRadius: "14px", border: "1px solid #e8e8f0", overflow: "hidden" }}>
      <div style={{ padding: "14px 18px", borderBottom: "1px solid #f0f0f8", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "7px" }}>
          <MdReceipt size={16} color="#3761E9" />
          <span style={{ fontSize: "13px", fontWeight: 700, color: "#1a1a2e" }}>Invoicing & Collection Tracking</span>
        </div>
        <button onClick={() => setAddingRow(true)} style={{ display: "flex", alignItems: "center", gap: "4px", padding: "6px 12px", border: "none", borderRadius: "8px", background: "#3761E9", color: "#fff", fontSize: "12px", fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
          <MdAdd size={14} /> Add Invoice
        </button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", borderBottom: "1px solid #f0f0f8" }}>
        {[
          { label: "Contract",  value: fmt(project.totalContractValue), color: "#3761E9" },
          { label: "Invoiced",  value: fmt(totalAmount),    color: "#FB8C00" },
          { label: "Collected", value: fmt(totalCollected), color: "#2E7D32" },
          { label: "Remaining", value: fmt(remaining),      color: remaining > 0 ? "#E53935" : "#2E7D32" },
        ].map(k => (
          <div key={k.label} style={{ padding: "12px 16px", borderRight: "1px solid #f0f0f8" }}>
            <p style={{ fontSize: "10px", color: "#aaa", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: "2px" }}>{k.label}</p>
            <p style={{ fontSize: "16px", fontWeight: 800, color: k.color }}>{k.value}</p>
          </div>
        ))}
      </div>

      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
          <thead>
            <tr style={{ background: "#f7f8ff" }}>
              {["Invoice #", "Issue Date", "Amount (SAR)", "Issued?", "Collected?", ""].map(h => (
                <th key={h} style={{ padding: "9px 14px", textAlign: "left", fontSize: "10px", fontWeight: 700, color: "#aaa", textTransform: "uppercase", letterSpacing: "0.4px", whiteSpace: "nowrap" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading
              ? <tr><td colSpan={6} style={{ padding: "30px", textAlign: "center", color: "#bbb" }}>Loading…</td></tr>
              : invoices.length === 0 && !addingRow
                ? <tr><td colSpan={6} style={{ padding: "30px", textAlign: "center", color: "#bbb" }}>No invoices yet</td></tr>
                : invoices.map(inv => (
                  <tr key={inv.id} style={{ borderTop: "1px solid #f5f5f5" }}
                    onMouseEnter={e => e.currentTarget.style.background = "#fafafa"}
                    onMouseLeave={e => e.currentTarget.style.background = ""}
                  >
                    <td style={{ padding: "10px 14px", fontWeight: 600, color: "#1a1a2e" }}>{inv.invoice_number}</td>
                    <td style={{ padding: "10px 14px", color: "#666" }}>{inv.issue_date ? fmtDate(inv.issue_date) : "—"}</td>
                    <td style={{ padding: "10px 14px", fontWeight: 700, color: "#3761E9" }}>{fmt(inv.amount)}</td>
                    <td style={{ padding: "10px 14px" }}>
                      <button onClick={() => toggle(inv, "is_issued")} style={{ padding: "3px 10px", borderRadius: "999px", border: "1.5px solid", borderColor: inv.is_issued ? "#2E7D32" : "#e0e0ee", background: inv.is_issued ? "#E8F5E9" : "#fff", color: inv.is_issued ? "#2E7D32" : "#aaa", fontSize: "11px", fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                        {inv.is_issued ? "✓ Yes" : "No"}
                      </button>
                    </td>
                    <td style={{ padding: "10px 14px" }}>
                      <button onClick={() => toggle(inv, "is_collected")} style={{ padding: "3px 10px", borderRadius: "999px", border: "1.5px solid", borderColor: inv.is_collected ? "#2E7D32" : "#e0e0ee", background: inv.is_collected ? "#E8F5E9" : "#fff", color: inv.is_collected ? "#2E7D32" : "#aaa", fontSize: "11px", fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                        {inv.is_collected ? "✓ Collected" : "Pending"}
                      </button>
                    </td>
                    <td style={{ padding: "10px 14px" }}>
                      <button onClick={() => handleDelete(inv.id)} style={{ background: "none", border: "none", cursor: "pointer", color: "#E53935", padding: "2px 4px" }}>✕</button>
                    </td>
                  </tr>
                ))
            }
            {addingRow && (
              <tr style={{ borderTop: "2px solid #3761E9" }}>
                <td style={{ padding: "8px 10px" }}><input value={newInv.invoice_number} onChange={e => setNewInv(p => ({ ...p, invoice_number: e.target.value }))} placeholder="INV-001" style={{ ...S, width: "80px" }} /></td>
                <td style={{ padding: "8px 10px" }}><input type="date" value={newInv.issue_date} onChange={e => setNewInv(p => ({ ...p, issue_date: e.target.value }))} style={{ ...S, width: "120px" }} /></td>
                <td style={{ padding: "8px 10px" }}><input type="number" value={newInv.amount} onChange={e => setNewInv(p => ({ ...p, amount: e.target.value }))} placeholder="Amount" style={{ ...S, width: "90px" }} /></td>
                <td colSpan={2} style={{ padding: "8px 10px" }}><input value={newInv.notes} onChange={e => setNewInv(p => ({ ...p, notes: e.target.value }))} placeholder="Notes" style={{ ...S, width: "140px" }} /></td>
                <td style={{ padding: "8px 10px" }}>
                  <div style={{ display: "flex", gap: "5px" }}>
                    <button onClick={handleAdd} disabled={saving} style={{ padding: "5px 12px", border: "none", borderRadius: "7px", background: "#3761E9", color: "#fff", fontSize: "11px", fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>{saving ? "…" : "Save"}</button>
                    <button onClick={() => setAddingRow(false)} style={{ padding: "5px 10px", border: "1px solid #e0e0ee", borderRadius: "7px", background: "#fff", color: "#666", fontSize: "11px", cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {invoices.length > 0 && (
        <div style={{ padding: "12px 18px", borderTop: "1px solid #f0f0f8", display: "flex", justifyContent: "flex-end", gap: "16px" }}>
          {unInvoiced > 0 && <span style={{ fontSize: "12px", color: "#FB8C00", fontWeight: 600 }}>⚠ Un-invoiced: {fmt(unInvoiced)}</span>}
          <span style={{ fontSize: "12px", color: "#2E7D32", fontWeight: 700 }}>✓ Collected: {fmt(totalCollected)}</span>
          <span style={{ fontSize: "12px", color: remaining > 0 ? "#E53935" : "#888", fontWeight: 600 }}>Remaining: {fmt(remaining)}</span>
        </div>
      )}
    </div>
  );
}

// ── Delete Confirmation ────────────────────────────────────
function ConfirmDeleteDialog({ title, onConfirm, onCancel, deleting }) {
  return createPortal(
    <div style={{ position: "fixed", inset: 0, zIndex: 10200, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}>
      <div style={{ background: "#fff", borderRadius: "16px", padding: "28px", maxWidth: "400px", width: "100%", boxShadow: "0 20px 60px rgba(0,0,0,0.25)" }}>
        <div style={{ fontSize: "32px", textAlign: "center", marginBottom: "12px" }}>⚠️</div>
        <h3 style={{ fontSize: "16px", fontWeight: 800, color: "#1a1a2e", textAlign: "center", marginBottom: "8px" }}>Delete Project?</h3>
        <p style={{ fontSize: "13px", color: "#666", textAlign: "center", lineHeight: 1.5, marginBottom: "20px" }}>
          "{title}" and all its data will be permanently deleted.
        </p>
        <div style={{ display: "flex", gap: "10px" }}>
          <button onClick={onCancel} style={{ flex: 1, padding: "10px", border: "1.5px solid #e0e0ee", borderRadius: "10px", background: "#fff", color: "#666", fontSize: "13px", fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
          <button onClick={onConfirm} disabled={deleting} style={{ flex: 1, padding: "10px", border: "none", borderRadius: "10px", background: "#E53935", color: "#fff", fontSize: "13px", fontWeight: 700, cursor: "pointer", fontFamily: "inherit", opacity: deleting ? 0.6 : 1 }}>
            {deleting ? "Deleting…" : "Yes, Delete"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ── Main Full-Page Component ───────────────────────────────
export default function ProjectDetailView() {
  const { id }      = useParams();
  const navigate    = useNavigate();

  const [project,    setProject]    = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [notFound,   setNotFound]   = useState(false);
  const [tab,        setTab]        = useState("overview");
  const [activities, setActivities] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [showEdit,   setShowEdit]   = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [deleting,   setDeleting]   = useState(false);

  // ── Fetch project from Supabase by id ──
  const fetchProject = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("projects")
        .select(`
          *,
          project_managers (
            employees:employee_id (id, full_name, job_title, avatar_url)
          ),
          project_time_entries (hours, employee_id),
          project_expenses (amount)
        `)
        .eq("id", id)
        .single();

      if (error || !data) { setNotFound(true); return; }

      // Compute financials
      let laborCost = 0;
      const empIds = [...new Set((data.project_time_entries || []).map(t => t.employee_id).filter(Boolean))];
      if (empIds.length > 0) {
        const { data: emps } = await supabase.from("employees").select("id, hourly_rate").in("id", empIds);
        const rateMap = {};
        (emps || []).forEach(e => { rateMap[e.id] = parseFloat(e.hourly_rate) || 0; });
        laborCost = (data.project_time_entries || []).reduce((s, t) => s + (parseFloat(t.hours) || 0) * (rateMap[t.employee_id] || 0), 0);
      }
      const externalCosts = (data.project_expenses || []).reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);

      setProject({
        id:                  data.id,
        title:               data.title              || "",
        clientName:          data.client_name        || "",
        totalContractValue:  parseFloat(data.total_contract_value) || 0,
        status:              data.status             || "Ongoing",
        startDate:           data.start_date         || "",
        deliveryDate:        data.delivery_date      || "",
        referrerName:        data.referrer_name      || "",
        referralPhone:       data.referral_phone     || "",
        referralRate:        parseFloat(data.referral_rate) || 0,
        assignedManagers:    (data.project_managers || []).map(m => {
          const e = m.employees;
          return e ? { id: e.id, name: e.full_name || "", jobTitle: e.job_title || "", avatar: e.avatar_url || null } : null;
        }).filter(Boolean),
        laborCost,
        externalCosts,
        forecastedProfit: (parseFloat(data.total_contract_value) || 0) - laborCost - externalCosts,
        createdAt: data.created_at || "",
      });
    } catch (err) {
      console.warn("[ProjectDetail] fetch:", err.message);
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchProject(); }, [fetchProject]);

  // Load current user & team
  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (user) {
        supabase.from("employees").select("id, full_name, avatar_url, job_title, hourly_rate").eq("id", user.id).single()
          .then(({ data }) => setCurrentUser(data || { id: user.id, full_name: user.email }));
      }
    });
    supabase.from("employees").select("id, full_name, job_title, avatar_url").eq("status", "Active")
      .then(({ data }) => setTeamMembers(data || []));
  }, []);

  const loadActivities = useCallback(async () => {
    if (!id) return;
    try { setActivities(await fetchActivities(id)); } catch {}
  }, [id]);

  useEffect(() => { if (tab === "activity") loadActivities(); }, [tab, loadActivities]);

  // Realtime updates
  useEffect(() => {
    if (!id) return;
    const ch = supabase.channel(`pdetail_page_${id}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "projects", filter: `id=eq.${id}` }, fetchProject)
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "project_activities", filter: `project_id=eq.${id}` }, loadActivities)
      .subscribe();
    return () => supabase.removeChannel(ch);
  }, [id, fetchProject, loadActivities]);

  const handleSaveEdit = async (data) => {
    try {
      const { error } = await supabase.from("projects").update({
        title:               data.title,
        client_name:         data.clientName,
        total_contract_value: parseFloat(data.totalContractValue) || 0,
        status:              data.status,
        start_date:          data.startDate || null,
        delivery_date:       data.deliveryDate || null,
        referrer_name:       data.referrerName || null,
        referral_phone:      data.referralPhone || null,
        referral_rate:       parseFloat(data.referralRate) || 0,
        updated_at:          new Date().toISOString(),
      }).eq("id", id);
      if (error) throw new Error(error.message);

      // Update managers
      await supabase.from("project_managers").delete().eq("project_id", id);
      if (data.assignedManagers?.length) {
        const unique = [...new Set(data.assignedManagers.map(m => m.id))];
        await supabase.from("project_managers").insert(unique.map(empId => ({ project_id: id, employee_id: empId })));
      }
      
      await logActivity({ projectId: id, employeeId: currentUser?.id, actionType: "update", description: `Updated project details` });
      
      setShowEdit(false);
      await fetchProject();
    } catch (err) { alert("Save failed: " + err.message); }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const { error } = await supabase.from("projects").delete().eq("id", id);
      if (error) throw new Error(error.message);
      navigate("/admin/projects");
    } catch (err) { alert("Delete failed: " + err.message); setDeleting(false); }
  };

  // ── Loading / Not Found ──
  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "60vh", fontFamily: "'DM Sans',sans-serif" }}>
        <div style={{ textAlign: "center", color: "#aaa" }}>
          <div style={{ fontSize: "32px", marginBottom: "12px" }}>⏳</div>
          <p style={{ fontSize: "14px" }}>Loading project…</p>
        </div>
      </div>
    );
  }

  if (notFound || !project) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "60vh", fontFamily: "'DM Sans',sans-serif" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: "48px", marginBottom: "12px" }}>🔍</div>
          <p style={{ fontSize: "16px", fontWeight: 700, color: "#1a1a2e", marginBottom: "6px" }}>Project not found</p>
          <p style={{ fontSize: "13px", color: "#888", marginBottom: "20px" }}>The project may have been deleted or the URL is incorrect.</p>
          <button onClick={() => navigate("/admin/projects")} style={{ padding: "10px 24px", border: "none", borderRadius: "10px", background: "#3761E9", color: "#fff", fontSize: "13px", fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
            ← Back to Projects
          </button>
        </div>
      </div>
    );
  }

  const cfg        = STATUS_CFG[project.status] || STATUS_CFG.Ongoing;
  const totalCost  = (project.laborCost || 0) + (project.externalCosts || 0);
  const foreProfit = (project.totalContractValue || 0) - totalCost;
  const commission = project.referralRate ? ((project.totalContractValue * project.referralRate) / 100) : 0;
  const contentWrapStyle = tab === "tasks"
    ? { padding: 0, maxWidth: "none", margin: 0 }
    : { padding: "24px 28px", maxWidth: "1200px", margin: "0 auto" };

  return (
    <div style={{ fontFamily: "'DM Sans',-apple-system,sans-serif", background: "#f7f8ff", minHeight: "100vh" }}>

      {/* ── Page Header ── */}
      <div style={{ background: "#fff", borderBottom: "1px solid #e8e8f0", padding: "16px 28px", position: "sticky", top: 0, zIndex: 100 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          {/* Left: back + title */}
          <div style={{ display: "flex", alignItems: "center", gap: "14px", flex: 1, minWidth: 0 }}>
            <button
              onClick={() => navigate("/admin/projects")}
              style={{ display: "flex", alignItems: "center", gap: "5px", padding: "7px 14px", border: "1.5px solid #e0e0ee", borderRadius: "9px", background: "#fff", color: "#555", fontSize: "12px", fontWeight: 600, cursor: "pointer", fontFamily: "inherit", flexShrink: 0 }}
            >
              <MdArrowBack size={15} /> Back to Projects
            </button>

            <div style={{ minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <h1 style={{ fontSize: "17px", fontWeight: 800, color: "#1a1a2e", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {project.title}
                </h1>
                <span style={{ background: cfg.bg, color: cfg.text, borderRadius: "999px", padding: "3px 10px", fontSize: "10px", fontWeight: 700, flexShrink: 0 }}>
                  {project.status}
                </span>
              </div>
              <p style={{ fontSize: "12px", color: "#888", marginTop: "2px" }}>{project.clientName || "No client"}</p>
            </div>
          </div>

          {/* Right: actions */}
          <div style={{ display: "flex", gap: "7px", flexShrink: 0, marginLeft: "16px" }}>
            <button onClick={() => setShowEdit(true)} style={{ display: "flex", alignItems: "center", gap: "5px", padding: "8px 16px", border: "1.5px solid #e0e0ee", borderRadius: "9px", background: "#fff", color: "#444", fontSize: "12px", fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
              <MdEdit size={14} /> Edit
            </button>
            <button onClick={() => setShowDelete(true)} style={{ display: "flex", alignItems: "center", gap: "5px", padding: "8px 14px", border: "1.5px solid #ffcdd2", borderRadius: "9px", background: "#ffebee", color: "#E53935", fontSize: "12px", fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
              <MdDelete size={14} /> Delete
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", gap: "2px", marginTop: "14px" }}>
          {TABS.map(t => {
            const Icon = t.icon;
            return (
              <button key={t.id} onClick={() => setTab(t.id)} style={{
                padding: "7px 14px", borderRadius: "8px", border: "none", cursor: "pointer",
                background: tab === t.id ? "#3761E9" : "transparent",
                color: tab === t.id ? "#fff" : "#888",
                fontSize: "12px", fontWeight: tab === t.id ? 700 : 400,
                fontFamily: "inherit", display: "flex", alignItems: "center", gap: "5px",
                transition: "all .15s",
              }}>
                <Icon size={14} /> {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Content ── */}
      <div style={contentWrapStyle}>

        {/* ── OVERVIEW ── */}
        {tab === "overview" && (
          <div style={{ display: "grid", gap: "18px" }}>

            {/* KPIs */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "16px" }}>
              {[
                { label: "Contract Value", value: fmt(project.totalContractValue), color: "#3761E9", bg: "#EEF2FF", tip: "Total revenue from the client" },
                { label: "Labor Cost",     value: fmt(project.laborCost),          color: "#E53935", bg: "#FFEBEE", tip: "Cost of employee time logged (Hours × Rate)" },
                { label: "Ext. Costs",     value: fmt(project.externalCosts),      color: "#FB8C00", bg: "#FFF8E1", tip: "Other project expenses (Materials, subs, etc.)" },
              ].map(m => (
                <div key={m.label} style={{ background: m.bg, borderRadius: "14px", padding: "20px 24px", position: "relative" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "8px" }}>
                    <p style={{ fontSize: "11px", color: m.color, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.5px" }}>{m.label}</p>
                    <div title={m.tip} style={{ cursor: "help", display: "flex" }}>
                      <MdInfoOutline size={14} color={m.color} style={{ opacity: 0.6 }} />
                    </div>
                  </div>
                  <p style={{ fontSize: "28px", fontWeight: 900, color: m.color }}>{m.value}</p>
                </div>
              ))}
            </div>

            {/* Profit Banner */}
            <div style={{ 
              background: foreProfit >= 0 ? "linear-gradient(135deg, #059669, #047857)" : "linear-gradient(135deg, #DC2626, #B91C1C)", 
              borderRadius: "16px", padding: "24px 32px", color: "#fff", 
              display: "flex", justifyContent: "space-between", alignItems: "center",
              boxShadow: foreProfit >= 0 ? "0 10px 25px -5px rgba(5, 150, 105, 0.3)" : "0 10px 25px -5px rgba(220, 38, 38, 0.3)"
            }}>
              <div>
                <p style={{ fontSize: "12px", color: "rgba(255,255,255,0.8)", textTransform: "uppercase", letterSpacing: "0.5px", fontWeight: 600, marginBottom: "4px" }}>Forecasted Profit</p>
                <p style={{ fontSize: "42px", fontWeight: 900, color: "#fff", lineHeight: 1 }}>{fmt(foreProfit)}</p>
              </div>
              <div style={{ textAlign: "right", background: "rgba(0,0,0,0.15)", padding: "12px 20px", borderRadius: "12px" }}>
                <p style={{ fontSize: "11px", color: "rgba(255,255,255,0.8)", textTransform: "uppercase", letterSpacing: "0.5px", fontWeight: 600, marginBottom: "2px" }}>Margin</p>
                <p style={{ fontSize: "28px", fontWeight: 900, color: "#fff", lineHeight: 1 }}>
                  {project.totalContractValue > 0 ? ((foreProfit / project.totalContractValue) * 100).toFixed(1) : 0}%
                </p>
              </div>
            </div>

            {/* Details */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "14px" }}>
              <div style={{ background: "#fff", borderRadius: "14px", padding: "18px", border: "1px solid #e8e8f0" }}>
                <p style={{ fontSize: "11px", color: "#aaa", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "12px" }}>Timeline</p>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
                  <span style={{ fontSize: "12px", color: "#888" }}>Start</span>
                  <span style={{ fontSize: "13px", fontWeight: 600, color: "#1a1a2e" }}>{fmtDate(project.startDate)}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ fontSize: "12px", color: "#888" }}>Delivery</span>
                  <span style={{ fontSize: "13px", fontWeight: 600, color: "#1a1a2e" }}>{fmtDate(project.deliveryDate)}</span>
                </div>
              </div>

              <div style={{ background: "#fff", borderRadius: "14px", padding: "18px", border: "1px solid #e8e8f0" }}>
                <p style={{ fontSize: "11px", color: "#aaa", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "12px" }}>Assigned Managers</p>
                {(project.assignedManagers || []).length === 0
                  ? <p style={{ fontSize: "12px", color: "#bbb" }}>No managers assigned</p>
                  : (project.assignedManagers || []).map((m, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                      <div style={{ width: "30px", height: "30px", borderRadius: "50%", background: "#3761E9", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "10px", fontWeight: 700, overflow: "hidden", flexShrink: 0 }}>
                        {m.avatar ? <img src={m.avatar} alt={m.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : initials(m.name)}
                      </div>
                      <div>
                        <p style={{ fontSize: "13px", fontWeight: 600, color: "#1a1a2e" }}>{m.name}</p>
                        <p style={{ fontSize: "10px", color: "#aaa" }}>{m.jobTitle}</p>
                      </div>
                    </div>
                  ))
                }
              </div>
            </div>

            {/* Referral */}
            {project.referrerName && (
              <div style={{ background: "#FFF8E1", borderRadius: "14px", padding: "18px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <p style={{ fontSize: "11px", color: "#E65100", fontWeight: 700 }}>🤝 Referral</p>
                  <p style={{ fontSize: "15px", fontWeight: 700, color: "#1a1a2e", marginTop: "2px" }}>{project.referrerName}</p>
                  {project.referralPhone && <p style={{ fontSize: "11px", color: "#888" }}>{project.referralPhone}</p>}
                </div>
                <div style={{ textAlign: "right" }}>
                  <p style={{ fontSize: "11px", color: "#888" }}>Commission ({project.referralRate}%)</p>
                  <p style={{ fontSize: "20px", fontWeight: 900, color: "#E65100" }}>{fmt(commission)}</p>
                </div>
              </div>
            )}

            {/* Billing */}
            <BillingSection project={project} currentUser={currentUser} />
          </div>
        )}

        {/* ── TIME SHEET ── */}
        {tab === "timesheet" && (
          <TimeSheetPanel
            project={project}
            onFinancialsChange={({ laborCost, extCosts }) =>
              setProject(p => ({ ...p, laborCost, externalCosts: extCosts, forecastedProfit: (p.totalContractValue || 0) - laborCost - extCosts }))
            }
          />
        )}

        {/* ── TASKS BOARD ── */}
        {tab === "tasks" && (
          <TaskBoard project={project} currentUser={currentUser} />
        )}

        {/* ── ACTIVITY ── */}
        {tab === "activity" && (
          <div style={{ display: "grid", gap: "12px", background: "#fff", borderRadius: "14px", padding: "24px", border: "1px solid #e8e8f0" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
              <h3 style={{ fontSize: "14px", fontWeight: 700, color: "#1a1a2e", display: "flex", alignItems: "center", gap: "6px" }}>
                <MdTimeline size={18} color="#3761E9" /> Recent Activity
              </h3>
              {activities.length > 0 && currentUser && (
                <button
                  onClick={async () => {
                    if (window.confirm("Are you sure you want to clear all activities for this project?")) {
                      await clearActivities(project.id);
                      loadActivities();
                    }
                  }}
                  style={{ display: "flex", alignItems: "center", gap: "4px", padding: "6px 12px", background: "#fff", border: "1px solid #e0e0ee", borderRadius: "8px", color: "#E53935", fontSize: "11px", fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}
                >
                  <MdClearAll size={14} /> Clear All
                </button>
              )}
            </div>
            {activities.length === 0
              ? <p style={{ textAlign: "center", color: "#bbb", fontSize: "13px", padding: "40px 0" }}>No activity yet</p>
              : activities.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).map((a, i) => {
                const action = a.action_type || 'update';
                
                let Icon = MdCheckCircle;
                let iconColor = "#3761E9";
                if (action === "create") { Icon = MdAddCircle; iconColor = "#2E7D32"; }
                else if (action === "update") { Icon = MdEdit; iconColor = "#FB8C00"; }
                else if (action === "delete") { Icon = MdDelete; iconColor = "#E53935"; }
                else if (action === "log_hours") { Icon = MdAccessTime; iconColor = "#3761E9"; }
                else if (action === "add_expense") { Icon = MdAttachMoney; iconColor = "#FB8C00"; }

                return (
                  <div key={a.id} style={{ display: "flex", gap: "12px", position: "relative" }}>
                    {/* Timeline line */}
                    {i !== activities.length - 1 && (
                      <div style={{ position: "absolute", left: "11px", top: "24px", bottom: "-12px", width: "2px", background: "#f0f0f8" }} />
                    )}
                    <div style={{ width: "24px", height: "24px", borderRadius: "50%", background: `${iconColor}15`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, zIndex: 1 }}>
                      <Icon size={14} color={iconColor} />
                    </div>
                    <div style={{ flex: 1, paddingBottom: "16px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                        <span style={{ fontSize: "13px", fontWeight: 500, color: "#1a1a2e", lineHeight: 1.4 }}>{a.description}</span>
                        <span style={{ fontSize: "11px", color: "#888", whiteSpace: "nowrap", marginLeft: "12px", background: "#f7f8ff", padding: "2px 8px", borderRadius: "999px" }}>
                          {relTime(a.created_at)}
                        </span>
                      </div>
                      {a.employees?.full_name && (
                        <div style={{ display: "flex", alignItems: "center", gap: "6px", marginTop: "6px" }}>
                          <div style={{ width: "18px", height: "18px", borderRadius: "50%", background: "#e0e0ee", color: "#666", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "8px", fontWeight: 700, overflow: "hidden" }}>
                            {a.employees.avatar_url ? <img src={a.employees.avatar_url} alt="avatar" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : initials(a.employees.full_name)}
                          </div>
                          <p style={{ fontSize: "11px", color: "#666" }}>{a.employees.full_name}</p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            }
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {showEdit && createPortal(
        <CreateProjectModal
          mode="edit"
          initialData={project}
          onClose={() => setShowEdit(false)}
          onSave={handleSaveEdit}
          teamMembers={teamMembers}
        />,
        document.body
      )}

      {/* Delete Confirmation */}
      {showDelete && (
        <ConfirmDeleteDialog
          title={project.title}
          deleting={deleting}
          onConfirm={handleDelete}
          onCancel={() => setShowDelete(false)}
        />
      )}
    </div>
  );
}
